import { useEffect } from 'react';

import toast from '#/components/common/toast';

import EthereumWallet from '#/features/ethereum-wallet/wallet';
import { logException } from '#/features/logging/logging';
import { useAuthActions } from '#/features/auth/auth-context';
import {
  useWalletActions,
  useWalletView,
} from '#/features/wallet/wallet-context';

import { sleep } from '#/utils/sleep';
import { UnauthorizedError } from '#/utils/errors';

import { translator } from '#/features/localization/utils';

import { userUnauthorizedTopic } from './auth-topics';

export function useJwtTokenExpirationHandler() {
  const authActions = useAuthActions();
  const walletActions = useWalletActions();
  const walletView = useWalletView();

  useEffect(() => {
    const handleUserUnauthorized = async () => {
      // Keep trying to get a token until we get an explicit authorization
      // error. Otherwise either getting a token will succeed or it will fail
      // due to conditions that should generally be temporary like network
      // issues or server errors, in which cases it should resolve itself.
      while (true) {
        const authRetryResult = await authActions.performAuth();
        if (authRetryResult.ok) return; // signature is still valid and no need to un-authenticate
        if (authRetryResult.error instanceof UnauthorizedError) break; // proceed to un-authenticate
        authActions.resetToken();
        await sleep(5000);
      }

      authActions.reset();
      walletActions.reset();
      EthereumWallet.disconnect().catch((err) => {
        const message =
          'Unexpected error disconnecting Ethereum' +
          ' wallet during JWT expiration handler';
        logException(new Error(message, { cause: err }));
        // Only reload in case of an error
        window.location.reload();
      });

      toast.warning(
        translator.t('Session expired, please connect your wallet again.'),
        {
          toastId: 'auth-token-expired',
          autoClose: false,
        },
      );
    };

    // NOTE: This could be called multiple times depending on the
    // number of unauthorized requests that are made after the JWT
    // token expiration. We only want to perform the auth once.
    const unsubscribe = userUnauthorizedTopic.subscribe(handleUserUnauthorized);

    return unsubscribe;
  }, [authActions, walletActions, walletView]);
}
